import React, { useEffect, useMemo, useState } from "react";
import Pagination from "../shared/Pagination";
import { InferredOccupancy } from "../../utils/ciFormat";
import ThinScroll from "../shared/ThinScroll";
import InfoTip from "../shared/InfoTip";
import { ErrorState } from "../shared/StateMessage";
import { SkeletonText } from "../shared/Skeleton";
import SelectMenu from "../shared/SelectMenu";
// fmtDateShort replaces a local fmtCheckIn that padded the day ("May 01"), which
// disagreed with every other CI panel ("May 1") for the same check-in date.
import { fmtRate, fmtDateShort, fmtDate, categoryLabel } from "./shared";
// Item 3 — competitor-group (p_tier) selector. The CI shared barrel
// (shared/components.js) re-exports PanelTakeaway + VerdictBlock but not
// GroupSelect, so import it directly from its module to keep this change
// self-contained and the import resolvable.
import GroupSelect from "./GroupSelect";

const PAGE_SIZE = 15;

// Column definitions for the rate-calendar table. Each entry drives both the
// header rendering and the comparator used when the user clicks the header.
// `defaultDir` is the direction applied on first click of a column (numbers
// land on descending so biggest values surface first; text/date stay
// ascending which is the natural reading order).
const COLUMNS = [
  { key: "date",      label: "Date",       align: "left",  defaultDir: "asc",
    compare: (rowA, rowB) => (rowA.check_in || "").localeCompare(rowB.check_in || "") },
  { key: "type",      label: "Type",       align: "left",  defaultDir: "asc",
    compare: (rowA, rowB) => (rowA.date_window_type || "").localeCompare(rowB.date_window_type || "") },
  { key: "site_type", label: "Site Type",  align: "left",  defaultDir: "asc",
    compare: (rowA, rowB) => (rowA.client_site_type_name || "").localeCompare(rowB.client_site_type_name || "") },
  { key: "your_rate", label: "Your Rate",  align: "right", defaultDir: "desc",
    compare: (rowA, rowB) => (rowA.client_rate ?? -Infinity) - (rowB.client_rate ?? -Infinity) },
  { key: "comp_avg",  label: "Market Avg", align: "right", defaultDir: "desc",
    compare: (rowA, rowB) => (rowA.comp_avg_rate ?? -Infinity) - (rowB.comp_avg_rate ?? -Infinity) },
  { key: "gap",       label: "Gap",        align: "right", defaultDir: "desc",
    compare: (rowA, rowB) => (rowA.gap_pct ?? -Infinity) - (rowB.gap_pct ?? -Infinity) },
  { key: "occupancy", label: "Your / Comp Occupancy", align: "right", defaultDir: "desc",
    compare: (rowA, rowB) => (rowA.client_occupancy_pct ?? -Infinity) - (rowB.client_occupancy_pct ?? -Infinity) },
  { key: "data_age",  label: "Data Age",   align: "right", defaultDir: "desc",
    // client_rate_as_of is an ISO timestamp; lexical compare = chronological for ISO 8601.
    compare: (rowA, rowB) => (rowA.client_rate_as_of || "").localeCompare(rowB.client_rate_as_of || "") },
];

const COLUMN_BY_KEY = Object.fromEntries(COLUMNS.map((column) => [column.key, column]));

const fmtGapPct = (value) => {
  if (value == null) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${Number(value).toFixed(1)}%`;
};

// Gap tone is a diverging scale: green above market, red below, neutral within
// ±5% (parity). All positive gaps read as good — no separate blue "premium" tier.
const gapTone = (value) => {
  if (value == null) return "neutral";
  if (value > 5) return "good";
  if (value < -5) return "bad";
  return "neutral";
};

const GAP_TONE_CLASS = {
  good: "text-emerald-600 dark:text-emerald-400",
  bad: "text-red-600 dark:text-red-400",
  neutral: "text-gray-600 dark:text-gray-400",
};


// Math.floor((now - client_rate_as_of) / 86400000) per spec — rates can be weeks old.
const fmtDataAge = (iso) => {
  if (!iso) return "—";
  const timestamp = new Date(iso).getTime();
  if (Number.isNaN(timestamp)) return "—";
  const days = Math.floor((Date.now() - timestamp) / 86400000);
  if (days <= 0) return "today";
  return `${days}d ago`;
};

// Stale = rate hasn't refreshed in >14d. Older quotes deserve a visual nudge.
const ageTone = (iso) => {
  if (!iso) return "muted";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days >= 14) return "stale";
  return "fresh";
};

const WINDOW_META = {
  weekend: { dotClass: "bg-[#32B1FF]", label: "Weekend" },
  weekday: { dotClass: "bg-gray-300 dark:bg-gray-600", label: "Weekday" },
  weekday_sample: { dotClass: "bg-gray-300 dark:bg-gray-600", label: "Weekday" },
  holiday: { dotClass: "bg-red-500", label: "Holiday" },
};

const TypeBadge = ({ type, name }) => {
  const meta = WINDOW_META[type];
  // Spec null-handling: when date_window_name is missing for non-standard dates, suppress
  // the badge entirely — the adjacent Date column already shows the check-in date.
  if (!meta && !name && !type) return <span className="text-gray-400 dark:text-gray-600">—</span>;
  const label = meta?.label ?? name ?? type;
  const dotClass = meta?.dotClass ?? "bg-gray-300 dark:bg-gray-600";
  return (
    <span className="inline-flex items-center gap-1.5" title={name || type || ""}>
      <span className={`inline-block w-2.5 h-2.5 rounded-sm ${dotClass}`} />
      <span className="text-gray-700 dark:text-gray-200">{label}</span>
    </span>
  );
};

// Item 3 — default competitor group (p_tier) for the active category. "direct"
// (Your competitors) when the category exposes a Direct tier, otherwise the
// first tier in the spec display order (airbnb → hotel → glampinghub → public).
// Mirrors GroupSelect's own option ordering so the default always maps to a
// visible option.
const GROUP_DEFAULT_ORDER = ["direct", "airbnb", "hotel", "glampinghub", "public"];
const defaultGroupFor = (categoryDetails) => {
  const byTier = categoryDetails?.properties_by_tier;
  if (!byTier) return null;
  const tiers = Array.isArray(byTier)
    ? byTier.map((row) => row?.tier).filter(Boolean)
    : Object.keys(byTier);
  for (const tier of GROUP_DEFAULT_ORDER) {
    if (tiers.includes(tier)) return tier;
  }
  return tiers[0] ?? null;
};

const HeaderRow = ({ count, siteType, siteTypeOptions, onSiteTypeChange, siteTypeDisabled, showSiteType, categoryDetails, selectedGroup, onGroupChange }) => (
  <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800 flex items-start justify-between gap-3 flex-wrap">
    <div>
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 inline-flex items-center">
        Rate Calendar
        <InfoTip text="Upcoming dates (next 90 days) with rate data. Each row compares your nightly rate vs the competitor average for that date — green gap = you're priced above market, red = below. Click any row to see every competitor's rate for that date." />
      </h2>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
        Upcoming dates (next 90 days) where rate data is available — yours vs. competitor average.
      </p>
    </div>
    <div className="flex items-center gap-3 flex-wrap">
      {/* Competitor group (p_tier) — drives the page's getCiRateCalendar
          refetch via onGroupChange → onTierChange. Self-hides when the active
          category exposes no tiers (GroupSelect returns null), but stays
          visible with a single option. */}
      <GroupSelect categoryDetails={categoryDetails} selectedGroup={selectedGroup} onChange={onGroupChange} />
      {showSiteType && (
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500 dark:text-gray-400">Site type:</span>
          <SelectMenu
            ariaLabel="Filter by site type"
            value={siteType ?? null}
            onChange={(value) => onSiteTypeChange?.(value)}
            disabled={siteTypeDisabled}
            placeholder="All site types"
            className="min-w-[14rem]"
            options={[
              { value: null, label: "All site types" },
              ...(siteTypeOptions ?? []).map((group) => ({
                value: group.normalized_group,
                label: `${group.normalized_group}${typeof group.date_windows === "number" ? ` (${group.date_windows})` : ""}`,
              })),
            ]}
          />
        </div>
      )}
      {typeof count === "number" && count > 0 && (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          <span className="font-semibold text-gray-700 dark:text-gray-200">{count}</span>{" "}
          {count === 1 ? "date" : "dates"} with data
        </div>
      )}
    </div>
  </div>
);

const SkeletonTable = ({ headerProps = {} }) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 overflow-hidden">
    <HeaderRow {...headerProps} />
    <div className="p-5">
      <SkeletonText lines={6} />
    </div>
  </div>
);

const ErrorBox = ({ message, headerProps = {} }) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-red-200 dark:border-red-900/50 overflow-hidden">
    <HeaderRow {...headerProps} />
    <ErrorState variant="inset" message={message} />
  </div>
);

const EmptyBox = ({ message, headerProps = {} }) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 overflow-hidden">
    <HeaderRow count={0} {...headerProps} />
    <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">{message}</div>
  </div>
);

const RateCalendarPanel = ({
  state,
  onRowClick,
  categoryDetails,
  onTierChange,
  siteType = null,
  siteTypeOptions = [],
  onSiteTypeChange,
  siteTypeDisabled = false,
  showSiteType = false,
}) => {
  // Page owns the *full* dataset (Pattern B: parallel-fetch-all). The panel
  // paginates client-side at PAGE_SIZE rows per page.
  const [page, setPage] = useState(1);
  // Item 3 — competitor group (p_tier). The page owns the rate-calendar fetch
  // and already threads `tier` through getCiRateCalendar, so we surface the
  // GroupSelect here and lift the selection up via `onTierChange`; the page
  // refetches with the new p_tier and feeds the result back through `state`
  // (only this panel's data changes — the rate-calendar effect is the sole
  // consumer of `selectedTier`). Default: "direct" (Your competitors) when
  // the active category has it, otherwise the first tier in display order
  // (airbnb → hotel → glampinghub → public). GroupSelect stays visible even
  // with a single option; it self-hides only when the category has no tiers.
  const [group, setGroup] = useState(null);
  useEffect(() => {
    const next = defaultGroupFor(categoryDetails);
    setGroup(next);
    onTierChange?.(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryDetails]);
  const handleGroupChange = (value) => {
    setGroup(value);
    onTierChange?.(value);
  };
  // Click-to-sort on column headers. Default = date asc (calendar forward),
  // which matches the order the backend returns rows in. Sort is applied to
  // the *full* dataset before pagination so click-to-sort surfaces the highest
  // (or lowest) value rows on page 1 instead of scattering them across 11 pages.
  const [sortBy, setSortBy] = useState("date");
  const [sortDir, setSortDir] = useState("asc");

  const payload = state.data;
  const rows = Array.isArray(payload) ? payload : (payload?.items ?? []);

  const sortedRows = useMemo(() => {
    const col = COLUMN_BY_KEY[sortBy];
    if (!col) return rows;
    const out = rows.slice().sort(col.compare);
    return sortDir === "desc" ? out.reverse() : out;
  }, [rows, sortBy, sortDir]);

  const total = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Snap back to page 1 whenever a fresh dataset arrives (filter change → refetch → new array reference).
  useEffect(() => { setPage(1); }, [rows]);
  // Also snap to page 1 on sort change so the user doesn't end up on page 5
  // of newly-reordered rows where the rows underneath no longer match
  // their mental model of "what's on page 5".
  useEffect(() => { setPage(1); }, [sortBy, sortDir]);

  const handleSort = (columnKey) => {
    const col = COLUMN_BY_KEY[columnKey];
    if (!col) return;
    if (sortBy === columnKey) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(columnKey);
      setSortDir(col.defaultDir);
    }
  };

  const headerProps = {
    siteType,
    siteTypeOptions,
    onSiteTypeChange,
    siteTypeDisabled,
    showSiteType,
    categoryDetails,
    selectedGroup: group,
    onGroupChange: handleGroupChange,
  };

  if (state.loading) return <SkeletonTable headerProps={headerProps} />;
  if (state.error) return <ErrorBox message={state.error} headerProps={headerProps} />;
  if (!rows.length) {
    const siteTypeActive = !!siteType;
    const message = siteTypeActive
      ? `No rate data for site type "${siteType}". Clear the site type filter or pick another from the dropdown.`
      : "No upcoming rate data in the next 90 days for this category. Try switching the category at the top of the page.";
    return <EmptyBox message={message} headerProps={headerProps} />;
  }

  // B2 — Category tab empty state: competitor data exists for this category but
  // the client has no rates in it. Surface the gap instead of rendering a blank
  // table. The tab itself stays visible (controlled by the page); we only swap
  // the panel body. Detect from data — don't rely on a categories_operated flag.
  const hasClientData = rows.some((row) => row.client_rate != null);
  if (!hasClientData) {
    const catCode = rows[0]?.category_code;
    const catName = catCode ? categoryLabel(catCode).toLowerCase() : "this category";
    return (
      <EmptyBox
        message={`Competitor ${catName} rates available. Your ${catName} inventory isn't in the system yet.`}
        headerProps={headerProps}
      />
    );
  }

  const start = (page - 1) * PAGE_SIZE;
  const pageRows = sortedRows.slice(start, start + PAGE_SIZE);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 overflow-hidden">
      <HeaderRow count={total} {...headerProps} />
      <ThinScroll className="overflow-x-auto responsive-card-table-wrapper">
        <table className="w-full min-w-[960px] text-sm table-fixed responsive-card-table">
          {/* colgroup drives table-fixed column widths. Inline style (not a
              Tailwind w-[X%] className) is used on <col> because Tailwind's
              arbitrary-width class is not reliably applied to <col> elements
              in this build — the same approach the sibling DynamicPricingPanel
              uses. Widths sum to 100% and give the numeric columns (Your Rate,
              Market Avg) enough room for "$1234.50"-style values so they no
              longer overflow past the column boundary. */}
          <colgroup>
            <col style={{ width: "9%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "20%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "12%" }} />
          </colgroup>
          <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              {COLUMNS.map((col, idx) => {
                const active = sortBy === col.key;
                const arrow = active ? (sortDir === "asc" ? "▲" : "▼") : null;
                const alignClass = col.align === "right" ? "text-right" : "text-left";
                const flexJustify = col.align === "right" ? "justify-end" : "justify-start";
                // Edge columns get extra outer padding so their content clears the
                // table's clipped container edges (first col left, last col right) and
                // lines up with the HeaderRow's px-5 gutters. Mirrored on the matching
                // <td> below so header and body stay aligned under table-layout: fixed.
                const padClass = idx === 0 ? "pl-5 pr-3" : idx === COLUMNS.length - 1 ? "pl-3 pr-5" : "px-3";
                return (
                  <th key={col.key} className={`${padClass} py-2.5 ${alignClass} font-semibold`} aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}>
                    <button
                      type="button"
                      onClick={() => handleSort(col.key)}
                      className={`inline-flex items-center gap-1.5 ${flexJustify} w-full uppercase tracking-wide font-semibold transition-colors ${active ? "text-[#32B1FF]" : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"} focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]/40 rounded-sm`}
                      title={`Sort by ${col.label}`}
                    >
                      <span>{col.label}</span>
                      <span className={`text-[10px] leading-none ${active ? "" : "hidden group-hover:inline-block"}`} aria-hidden="true">{arrow ?? "▾"}</span>
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, index) => {
              const tone = gapTone(row.gap_pct);
              const stale = ageTone(row.client_rate_as_of) === "stale";
              const clickable = typeof onRowClick === "function";
              return (
                <tr
                  key={`${row.check_in}-${index}`}
                  onClick={clickable ? () => onRowClick(row) : undefined}
                  onKeyDown={clickable ? (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onRowClick(row); } } : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  role={clickable ? "button" : undefined}
                  className={`border-b dark:border-gray-800 transition-colors ${clickable ? "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-800" : "hover:bg-gray-50 dark:hover:bg-gray-800/50"}`}
                >
                  <td data-label="Date" className="pl-5 pr-3 py-2.5 text-gray-900 dark:text-gray-100 whitespace-nowrap">
                    <div>
                      <div className="font-medium">{fmtDateShort(row.check_in)}</div>
                      {row.nights > 1 && (
                        <div className="text-[11px] text-gray-500 dark:text-gray-400">{row.nights} nights</div>
                      )}
                    </div>
                  </td>
                  <td data-label="Type" className="px-3 py-2.5 whitespace-nowrap overflow-hidden">
                    <TypeBadge type={row.date_window_type} name={row.date_window_name} />
                  </td>
                  <td data-label="Site Type" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 overflow-hidden">
                    {row.client_site_type_name ? (
                      <div className="truncate" title={row.client_site_type_name}>{row.client_site_type_name}</div>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-600">—</span>
                    )}
                  </td>
                  <td data-label="Your Rate" className="px-3 py-2.5 text-right text-gray-900 dark:text-gray-100 whitespace-nowrap overflow-hidden">
                    <div className="font-medium truncate">{fmtRate(row.client_rate)}</div>
                  </td>
                  <td data-label="Comp Avg" className="px-3 py-2.5 text-right text-gray-900 dark:text-gray-100 whitespace-nowrap">
                    <div>
                      <div>{fmtRate(row.comp_avg_rate)}</div>
                      {typeof row.competitor_count === "number" && (
                        <div className="text-[11px] text-gray-500 dark:text-gray-400">
                          {row.competitor_count} comp{row.competitor_count === 1 ? "" : "s"}
                        </div>
                      )}
                    </div>
                  </td>
                  <td data-label="Gap" className={`px-3 py-2.5 text-right font-semibold whitespace-nowrap ${GAP_TONE_CLASS[tone]}`}>
                    {fmtGapPct(row.gap_pct)}
                  </td>
                  <td data-label="Occ You/Comp" className="px-3 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    <span>
                      <InferredOccupancy value={row.client_occupancy_pct} /> / <InferredOccupancy value={row.comp_avg_occupancy_pct} />
                    </span>
                  </td>
                  <td
                    data-label="Data Age"
                    className={`pl-3 pr-5 py-2.5 text-right whitespace-nowrap ${
                      stale ? "text-amber-600 dark:text-amber-400" : "text-gray-500 dark:text-gray-400"
                    }`}
                  >
                    <InfoTip text={row.client_rate_as_of ? fmtDate(row.client_rate_as_of) : ""}>
                      <span>{fmtDataAge(row.client_rate_as_of)}</span>
                    </InfoTip>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </ThinScroll>

      {totalPages > 1 && (
        <div className="px-5 pt-1 pb-3 border-t border-gray-200 dark:border-gray-800">
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalItems={total}
            perPage={PAGE_SIZE}
            label="dates"
          />
        </div>
      )}
    </div>
  );
};

export default RateCalendarPanel;
